import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, IconButton, Stack, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MessageIcon from "@mui/icons-material/Message";

import Typography from "components/basic/typography";

import { initials } from "../../helpers/initials";
import { statusMeta, proposedText } from "../../helpers/attendeeStatus";
import { Attendee } from "../../../../types";

const AttendeeRow: React.FC<{
  attendee: Attendee;
  hasQuotes?: boolean;
  onViewQuotes?: () => void;
  onMessage?: () => void;
  onApplyProposedTime?: (date: string, from: string, to: string) => void;
}> = ({
  attendee,
  hasQuotes,
  onViewQuotes,
  onMessage,
  onApplyProposedTime,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const meta = statusMeta(attendee.status, theme);
  const proposal = proposedText(attendee);

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
        {initials(attendee.name || attendee.email)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          {attendee.name || attendee.email}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <meta.Icon sx={{ fontSize: 14, color: meta.color }} />
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {meta.label}
            {attendee.optional ? t("details.optional") : ""}
          </Typography>
        </Stack>
        {proposal && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Typography
              variant="caption"
              style={{ color: theme.palette.warning.main }}
            >
              {t("details.proposed", { time: proposal })}
            </Typography>
            {onApplyProposedTime && (
              <Box
                component="button"
                onClick={() =>
                  onApplyProposedTime(
                    attendee.proposedDate as string,
                    attendee.proposedFrom as string,
                    attendee.proposedTo as string,
                  )
                }
                sx={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  fontSize: "0.75rem",
                  textDecoration: "underline",
                  p: 0,
                }}
              >
                {t("details.use_time")}
              </Box>
            )}
          </Stack>
        )}
      </Box>
      {hasQuotes && (
        <IconButton
          size="small"
          title={t("details.view_quotes")}
          onClick={onViewQuotes}
          sx={{ color: theme.palette.info.main }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      )}
      {onMessage && (
        <IconButton
          size="small"
          title={t("details.message")}
          onClick={onMessage}
          sx={{ color: theme.palette.warning.main }}
        >
          <MessageIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
};

export default AttendeeRow;
